import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { pdfActivityService } from './pdfActivityService';
import { backendConfig } from '../config/backendConfig';

// Daily activities data structure
const dailyActivities = [
  {
    id: 1,
    title: "Draw Your Favorite Animal",
    description: "Create a colorful drawing of your favorite animal. Think about what makes this animal special to you!",
    instructions: "1. Choose your favorite animal\n2. Draw it with bright colors\n3. Add a background\n4. Share what you love about this animal",
    duration: "20 minutes",
    materials: "Paper, pencils, crayons, or markers",
    difficulty: "Beginner",
    category: "Drawing",
    tips: "Try to capture the animal's personality and unique features!"
  },
  {
    id: 2,
    title: "Create a Rainbow Garden",
    description: "Design a magical garden with flowers in every color of the rainbow!",
    instructions: "1. Draw different types of flowers\n2. Use all rainbow colors\n3. Add butterflies or bees\n4. Create a magical background",
    duration: "20 minutes",
    materials: "Paper, colored pencils, or paint",
    difficulty: "Beginner",
    category: "Painting",
    tips: "Think about what magical creatures might live in your garden!"
  },
  {
    id: 3,
    title: "Design Your Dream House",
    description: "Imagine and draw your perfect dream house with all the features you'd love!",
    instructions: "1. Draw the house structure\n2. Add windows and doors\n3. Include special rooms\n4. Add fun details like a pool or treehouse",
    duration: "20 minutes",
    materials: "Paper, pencils, markers",
    difficulty: "Intermediate",
    category: "Architecture",
    tips: "Don't forget to add your favorite colors and fun features!"
  },
  {
    id: 4,
    title: "Paint a Sunset",
    description: "Capture the beautiful colors of a sunset in your own unique way!",
    instructions: "1. Start with the sky colors\n2. Add the sun\n3. Create silhouettes in the foreground\n4. Blend colors smoothly",
    duration: "20 minutes",
    materials: "Paper, paint, brushes",
    difficulty: "Beginner",
    category: "Painting",
    tips: "Use warm colors like red, orange, and yellow for the sunset!"
  },
  {
    id: 5,
    title: "Create a Comic Strip",
    description: "Tell a short story through pictures in a comic strip format!",
    instructions: "1. Plan your story (3-4 panels)\n2. Draw the characters\n3. Add speech bubbles\n4. Color your comic",
    duration: "20 minutes",
    materials: "Paper, pencils, markers, rulers",
    difficulty: "Intermediate",
    category: "Comics",
    tips: "Keep your story simple and fun!"
  }
  // Add more activities from your PDF here
];

// Simple in-memory cache for activities (cleared on page refresh)
let activitiesCache = null;
let activitiesCacheTime = null;
/** Bump when activity id resolution logic changes (invalidates stale cached ids). */
const ACTIVITIES_CACHE_VERSION = 5;

const STORAGE_ACTIVITY_PAGE_BASE =
  'https://storage.googleapis.com/artmosphere-89b64.firebasestorage.app/activities';
const CACHE_DURATION = 30 * 1000; // 30 seconds (reduced from 5 minutes to ensure fresh data)

/** Parse "Daily Activity 33" / "Daily Art Activity 33" → 33 */
export function parseDailyActivityNumberFromTitle(title) {
  if (!title || typeof title !== 'string') return null;
  const m =
    title.match(/(?:daily\s+)?(?:art\s+)?activity\s*#?\s*(\d+)/i) ||
    title.match(/art\s+of\s+the\s+day\s*#?\s*(\d+)/i);
  return m ? Number(m[1]) : null;
}

/** e.g. imageUrl containing page_33.png → 33 */
export function parsePageNumberFromImageUrl(imageUrl) {
  if (!imageUrl) return null;
  const m = String(imageUrl).match(/page[_-]?(\d+)/i);
  return m ? Number(m[1]) : null;
}

/**
 * Canonical activity number for UI/PDF/deep-links.
 * When title says "Daily Activity 33" but number field is 1, trust the title.
 */
export function resolveCanonicalActivityId(data = {}, docId = '') {
  const fieldNum =
    data.number != null && data.number !== '' ? Number(data.number) : null;
  const titleNum = parseDailyActivityNumberFromTitle(data.title || '');
  const pageNum = parsePageNumberFromImageUrl(data.imageUrl || '');
  const titleHasExplicitNumber = /(?:daily\s+)?(?:art\s+)?activity\s*#?\s*\d+/i.test(
    data.title || ''
  );

  if (titleNum != null && !Number.isNaN(titleNum)) {
    if (
      titleHasExplicitNumber &&
      (fieldNum == null || Number.isNaN(fieldNum) || fieldNum !== titleNum)
    ) {
      return titleNum;
    }
    if (fieldNum != null && !Number.isNaN(fieldNum) && fieldNum === titleNum) {
      return fieldNum;
    }
    if (fieldNum == null || Number.isNaN(fieldNum)) return titleNum;
  }

  if (fieldNum != null && !Number.isNaN(fieldNum) && fieldNum >= 1 && fieldNum <= 999) {
    return fieldNum;
  }
  if (pageNum != null && !Number.isNaN(pageNum)) return pageNum;
  return docId;
}

/** Deep-link / museum card: always use the requested curriculum number for UI + PDF. */
export function applyRequestedActivityNumber(activity, requestedNumber) {
  const asNum = Number(requestedNumber);
  if (!activity || Number.isNaN(asNum) || asNum < 1) return activity;

  const pageUrl = `${STORAGE_ACTIVITY_PAGE_BASE}/page_${asNum}.png`;
  const existingPage = parsePageNumberFromImageUrl(activity.imageUrl);

  return {
    ...activity,
    id: asNum,
    number: asNum,
    title:
      parseDailyActivityNumberFromTitle(activity.title || '') === asNum
        ? activity.title
        : activity.title || `Daily Activity ${asNum}`,
    imageUrl: existingPage === asNum ? activity.imageUrl : pageUrl,
  };
}

export function buildActivityFromFirestoreDoc(docSnap) {
  const data = docSnap.data() || {};
  const canonicalId = resolveCanonicalActivityId(data, docSnap.id);
  return {
    id: canonicalId,
    number: canonicalId,
    rawNumber: data.number,
    title: data.title || `Daily Activity ${canonicalId}`,
    docId: docSnap.id,
    description: data.description || 'Express your creativity with this engaging art activity',
    instructions: data.content ? data.content.join('\n') : 'Follow the instructions in the activity image to create your artwork',
    duration: data.duration || '20 minutes',
    materials: data.materials || 'Paper, pencils, crayons, markers, or any art supplies you prefer',
    difficulty: data.difficulty || 'Beginner',
    category: data.category || 'Creative Expression',
    tips: 'Let your imagination guide you and enjoy the creative process!',
    imageUrl: data.imageUrl || '',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

/** Match by activity number — NOT by Firestore doc id when request is numeric (e.g. "33"). */
export function findDailyActivityFromList(activities, requestedId) {
  if (!requestedId || !Array.isArray(activities) || activities.length === 0) return null;
  const raw = String(requestedId).trim();
  const asNum = Number(raw);
  const isNumericRequest = /^\d+$/.test(raw);

  const predicates = [
    (a) => String(a.id) === raw,
    (a) => a.number != null && String(a.number) === raw,
    (a) => !Number.isNaN(asNum) && Number(a.id) === asNum,
    (a) => !Number.isNaN(asNum) && Number(a.number) === asNum,
    (a) => {
      const titleNum = parseDailyActivityNumberFromTitle(a.title || '');
      return !Number.isNaN(asNum) && titleNum === asNum;
    },
    (a) => {
      const pageNum = parsePageNumberFromImageUrl(a.imageUrl);
      return !Number.isNaN(asNum) && pageNum === asNum;
    },
    (a) => a.rawNumber != null && String(a.rawNumber) === raw,
    (a) => !Number.isNaN(asNum) && Number(a.rawNumber) === asNum,
  ];

  if (!isNumericRequest) {
    predicates.push((a) => a.docId && String(a.docId) === raw);
  }

  for (const pred of predicates) {
    const found = activities.find(pred);
    if (found) {
      if (isNumericRequest && !Number.isNaN(asNum)) {
        return applyRequestedActivityNumber(found, asNum);
      }
      return found;
    }
  }
  return null;
}

export const dailyActivityService = {
  findDailyActivityFromList,

  // Get all available activities from Firebase activities collection
  getAllActivities: async (forceRefresh = false) => {
    // Return cached activities if available and fresh (unless force refresh)
    if (
      !forceRefresh &&
      activitiesCache?._cacheVersion === ACTIVITIES_CACHE_VERSION &&
      activitiesCacheTime &&
      Date.now() - activitiesCacheTime < CACHE_DURATION
    ) {
      console.log('✅ Using cached activities');
      return activitiesCache.activities;
    }
    try {
      console.log('🔄 Loading activities from Firebase activities collection...');
      
      // Query Firebase activities collection for daily_activity type
      const activitiesQuery = query(
        collection(db, 'activities'),
        where('type', '==', 'daily_activity')
      );
      
      const querySnapshot = await getDocs(activitiesQuery);
      const activities = [];
      
      querySnapshot.forEach((docSnap) => {
        activities.push(buildActivityFromFirestoreDoc(docSnap));
      });
      
      // Sort activities by canonical numeric id when possible
      activities.sort((a, b) => {
        const ai = Number(a.id);
        const bi = Number(b.id);
        if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;
        return String(a.id).localeCompare(String(b.id));
      });
      
      console.log('✅ Loaded activities from Firebase:', activities.length, 'activities');
      console.log('📋 Activities found:', activities.map(a => ({ id: a.id, title: a.title, docId: a.docId })));
      
      // Cache the activities
      if (activities.length > 0) {
        activitiesCache = { _cacheVersion: ACTIVITIES_CACHE_VERSION, activities };
        activitiesCacheTime = Date.now();
        console.log('💾 Activities cached for', CACHE_DURATION / 1000, 'seconds');
      }
      
      // If no activities found in Firebase, use Storage-based activities
      if (activities.length === 0) {
        console.log('🔄 No activities found in Firebase, using Storage-based activities...');
        
        // Dynamic list of activities - add new ones here when you upload them
        const storageActivities = [
          {
            id: 1,
            title: "Daily Art Activity 1",
            description: "Express your creativity with this engaging art activity",
            instructions: "Follow the instructions in the activity image to create your artwork",
            duration: "20 minutes",
            materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
            difficulty: "Beginner",
            category: "Creative Expression",
            tips: "Let your imagination guide you and enjoy the creative process!",
            imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-08-29%20at%206.04.47%20PM.png?alt=media',
            filename: "Screenshot 2025-08-29 at 6.04.47 PM.png"
          },
          {
            id: 2,
            title: "Daily Art Activity 2", 
            description: "Continue your creative journey with this second activity",
            instructions: "Follow the instructions in the activity image to create your artwork",
            duration: "20 minutes",
            materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
            difficulty: "Beginner",
            category: "Creative Expression",
            tips: "Let your imagination guide you and enjoy the creative process!",
            imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2Ffinal%20App%20version_Daily%20Acts.png?alt=media',
            filename: "final App version_Daily Acts.png"
          },
          {
            id: 3,
            title: "Daily Art Activity 3",
            description: "Explore new creative techniques with this third activity",
            instructions: "Follow the instructions in the activity image to create your artwork",
            duration: "20 minutes",
            materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
            difficulty: "Beginner",
            category: "Creative Expression",
            tips: "Let your imagination guide you and enjoy the creative process!",
            imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-09-05%20at%205.37.28%20PM.png?alt=media',
            filename: "Screenshot 2025-09-05 at 5.37.28 PM.png"
          },
          {
            id: 4,
            title: "Daily Art Activity 4",
            description: "Explore new creative techniques with this fourth activity",
            instructions: "Follow the instructions in the activity image to create your artwork",
            duration: "20 minutes",
            materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
            difficulty: "Beginner",
            category: "Creative Expression",
            tips: "Let your imagination guide you and enjoy the creative process!",
            imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-09-05%20at%206.02.45%20PM.png?alt=media',
            filename: "Screenshot 2025-09-05 at 6.02.45 PM.png"
          }
          // Add new activities here when you upload them to Firebase Storage
          // Example:
          // {
          //   id: 4,
          //   title: "Daily Art Activity 4",
          //   description: "Your new activity description",
          //   instructions: "Follow the instructions in the activity image to create your artwork",
          //   duration: "20 minutes",
          //   materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
          //   difficulty: "Beginner",
          //   category: "Creative Expression",
          //   tips: "Let your imagination guide you and enjoy the creative process!",
          //   imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2Fyour-new-filename.png?alt=media',
          //   filename: "your-new-filename.png"
          // }
        ];
        
        console.log('✅ Using Storage-based activities:', storageActivities.length);
        // Cache storage activities too
        activitiesCache = { _cacheVersion: ACTIVITIES_CACHE_VERSION, activities: storageActivities };
        activitiesCacheTime = Date.now();
        return storageActivities;
      }
      
      return activities;
      
    } catch (error) {
      console.error('❌ Error loading activities from Firebase:', error);
      console.log('🔄 Falling back to hardcoded activities...');
      
      // Fallback: Create activities from Firebase Storage files
      console.log('🔄 Creating activities from Firebase Storage files...');
      
      const storageActivities = [
        {
          id: 1,
          title: "Daily Art Activity 1",
          description: "Express your creativity with this engaging art activity",
          instructions: "Follow the instructions in the activity image to create your artwork",
          duration: "20 minutes",
          materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
          difficulty: "Beginner",
          category: "Creative Expression",
          tips: "Let your imagination guide you and enjoy the creative process!",
          imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-08-29%20at%206.04.47%20PM.png?alt=media',
          filename: "Screenshot 2025-08-29 at 6.04.47 PM.png"
        },
        {
          id: 2,
          title: "Daily Art Activity 2", 
          description: "Continue your creative journey with this second activity",
          instructions: "Follow the instructions in the activity image to create your artwork",
          duration: "20 minutes",
          materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
          difficulty: "Beginner",
          category: "Creative Expression",
          tips: "Let your imagination guide you and enjoy the creative process!",
          imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2Ffinal%20App%20version_Daily%20Acts.png?alt=media',
          filename: "final App version_Daily Acts.png"
        },
        {
          id: 3,
          title: "Daily Art Activity 3",
          description: "Explore new creative techniques with this third activity",
          instructions: "Follow the instructions in the activity image to create your artwork",
          duration: "20 minutes",
          materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
          difficulty: "Beginner",
          category: "Creative Expression",
          tips: "Let your imagination guide you and enjoy the creative process!",
          imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-09-05%20at%205.37.28%20PM.png?alt=media',
          filename: "Screenshot 2025-09-05 at 5.37.28 PM.png"
        },
        {
          id: 4,
          title: "Daily Art Activity 4",
          description: "Explore new creative techniques with this fourth activity",
          instructions: "Follow the instructions in the activity image to create your artwork",
          duration: "20 minutes",
          materials: "Paper, pencils, crayons, markers, or any art supplies you prefer",
          difficulty: "Beginner",
          category: "Creative Expression",
          tips: "Let your imagination guide you and enjoy the creative process!",
          imageUrl: 'https://firebasestorage.googleapis.com/v0/b/artmosphere-89b64.firebasestorage.app/o/activities%2FScreenshot%202025-09-05%20at%206.02.45%20PM.png?alt=media',
          filename: "Screenshot 2025-09-05 at 6.02.45 PM.png"
        }
      ];
      
      console.log('✅ Created activities from Storage files:', storageActivities.length);
      // Cache fallback activities
      activitiesCache = { _cacheVersion: ACTIVITIES_CACHE_VERSION, activities: storageActivities };
      activitiesCacheTime = Date.now();
      return storageActivities;
    }
  },

  // Get all shared submissions by user (public daily activity submissions)
  getUserSharedSubmissions: async (userId) => {
    try {
      const q = query(
        collection(db, 'submissions'),
        where('uid', '==', userId),
        where('isPublic', '==', true)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const at = a.createdAt?.toMillis?.() || 0;
          const bt = b.createdAt?.toMillis?.() || 0;
          return bt - at;
        });
      return items;
    } catch (error) {
      console.error('❌ Error fetching user shared submissions:', error);
      return [];
    }
  },

  // Get a specific activity by ID
  getActivityById: async (id) => {
    try {
      const activities = await dailyActivityService.getAllActivities();
      return activities.find(activity => activity.id === id);
    } catch (error) {
      console.error('Error getting activity by ID:', error);
    return dailyActivities.find(activity => activity.id === id);
    }
  },

  // Get user's daily activity progress via backend (with timeout and caching)
  getUserProgress: async (userId) => {
    try {
      console.log('🔄 Getting user progress via backend for:', userId);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch(`http://localhost:3001/api/user-progress/${userId}?_t=${Date.now()}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get user progress');
      }

      const result = await response.json();
      console.log('✅ User progress loaded via backend:', result.progress);
      return result.progress;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⚠️ Backend progress check timed out, using fallback');
        // Return empty progress instead of throwing
        return {
          completedActivities: [],
          totalActivities: 0,
          currentDay: 1
        };
      }
      console.error('❌ Error getting user progress via backend:', error);
      // Return empty progress instead of throwing to prevent blocking
      return {
        completedActivities: [],
        totalActivities: 0,
        currentDay: 1
      };
    }
  },

  // Complete an activity via backend
  completeActivity: async (userId, activityId) => {
    try {
      console.log('🔄 Completing activity via backend:', { userId, activityId });
      
      // First get current progress
      const currentProgress = await dailyActivityService.getUserProgress(userId);
      
      // Get activity details
      const activities = await dailyActivityService.getAllActivities();
      // Handle both string and number IDs consistently
      const activity = activities.find(a => String(a.id) === String(activityId));
      
      if (!activity) {
        throw new Error('Activity not found');
      }
      
      // Check if this activity is already completed (handle both string and number IDs)
      const alreadyCompleted = currentProgress.completedActivities?.some(
        completed => String(completed.activityId) === String(activityId)
      );
      
      if (alreadyCompleted) {
        throw new Error('Activity already completed');
      }
      
      // Calculate next activity time (24 hours from now)
      const nextActivityTime = new Date(Date.now() + 24 * 60 * 60 * 1000);
      
      // Update progress
      const updatedProgress = {
        ...currentProgress,
        currentDay: currentProgress.currentDay + 1,
        completedActivities: [
          ...(currentProgress.completedActivities || []),
          {
            activityId: Number(activityId) || activityId, // Ensure consistent ID format
            completedAt: new Date(),
            title: activity.title,
            category: activity.category
          }
        ],
        lastCompletedAt: new Date(),
        nextActivityTime: nextActivityTime
      };
      
      // Send updated progress to backend
      const backendUrl = backendConfig.baseUrl;
      const userProgressUrl = `${backendUrl}/api/user-progress/${userId}`;
      
      const response = await fetch(userProgressUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ progress: updatedProgress })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update progress');
      }

      console.log('✅ Activity completed via backend');
      return updatedProgress;
      
    } catch (error) {
      console.error('❌ Error completing activity via backend:', error);
      throw error;
    }
  },

  // Check if user can access next activity
  canAccessNextActivity: (userProgress) => {
    if (!userProgress || !userProgress.nextActivityTime) {
      return true; // First activity is always available
    }
    
    const now = new Date();
    const nextActivityTime = userProgress.nextActivityTime.toDate();
    
    return now >= nextActivityTime;
  },

  // Get current available activity for user
  getCurrentActivity: async (userProgress) => {
    try {
      console.log('🔄 Getting current activity for user progress:', userProgress);
      const activities = await dailyActivityService.getAllActivities();
      console.log('📋 Available activities:', activities.length);
      
      if (!userProgress) {
        return activities[0]; // First activity
      }
      
      const canAccessNext = dailyActivityService.canAccessNextActivity(userProgress);
      
      if (canAccessNext) {
        const nextActivityId = userProgress.currentDay;
        const activity = activities.find(a => a.id === nextActivityId);
        
        if (activity) {
          return activity;
        } else {
          // All activities completed
          return {
            ...activities[activities.length - 1],
            completed: true,
            title: "All Activities Completed! 🎉"
          };
        }
      } else {
        // Show the last completed activity or current locked activity
        const currentActivityId = userProgress.currentDay - 1;
        return activities.find(a => a.id === currentActivityId) || activities[0];
      }
    } catch (error) {
      console.error('Error getting current activity:', error);
      // Fallback to hardcoded activities
    if (!userProgress) {
        return dailyActivities[0];
    }
    
    const canAccessNext = dailyActivityService.canAccessNextActivity(userProgress);
    
    if (canAccessNext) {
      const nextActivityId = userProgress.currentDay;
      const activity = dailyActivities.find(a => a.id === nextActivityId);
      
      if (activity) {
        return activity;
      } else {
        return {
          ...dailyActivities[dailyActivities.length - 1],
          completed: true,
          title: "All Activities Completed! 🎉"
        };
      }
    } else {
      const currentActivityId = userProgress.currentDay - 1;
      return dailyActivities.find(a => a.id === currentActivityId) || dailyActivities[0];
      }
    }
  },

  // Get time until next activity
  getTimeUntilNext: (userProgress) => {
    if (!userProgress || !userProgress.nextActivityTime) {
      return null;
    }
    
    const now = new Date();
    const nextActivityTime = userProgress.nextActivityTime.toDate();
    const timeDiff = nextActivityTime.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return null; // Available now
    }
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { hours, minutes };
  },

  // Get user's completion statistics
  getUserStats: async (userProgress) => {
    try {
      const activities = await dailyActivityService.getAllActivities();
      const total = activities.length;
      
    if (!userProgress) {
      return {
        completed: 0,
          total: total,
        percentage: 0,
        streak: 0
      };
    }
    
    const completed = userProgress.completedActivities?.length || 0;
    const percentage = Math.round((completed / total) * 100);
    
    // Calculate streak (consecutive days)
    let streak = 0;
    if (userProgress.completedActivities && userProgress.completedActivities.length > 0) {
      // Simple streak calculation - can be enhanced
      streak = Math.min(completed, 7); // For now, assume max 7 day streak
    }
    
    return {
      completed,
      total,
      percentage,
      streak
    };
    } catch (error) {
      console.error('Error getting user stats:', error);
      // Fallback to hardcoded activities
      if (!userProgress) {
        return {
          completed: 0,
          total: dailyActivities.length,
          percentage: 0,
          streak: 0
        };
      }
      
      const completed = userProgress.completedActivities?.length || 0;
      const total = dailyActivities.length;
      const percentage = Math.round((completed / total) * 100);
      
      let streak = 0;
      if (userProgress.completedActivities && userProgress.completedActivities.length > 0) {
        streak = Math.min(completed, 7);
      }
      
      return {
        completed,
        total,
        percentage,
        streak
      };
    }
  }
};

/** Load one daily activity by curriculum number (museum / submission activityId). */
dailyActivityService.fetchActivityByNumber = async function fetchActivityByNumber(activityNumber) {
  const raw = String(activityNumber ?? '').trim();
  const asNum = Number(raw);
  if (!raw || Number.isNaN(asNum)) return null;

  for (const numVal of [asNum, raw]) {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'activities'),
          where('type', '==', 'daily_activity'),
          where('number', '==', numVal)
        )
      );
      if (!snap.empty) {
        return applyRequestedActivityNumber(buildActivityFromFirestoreDoc(snap.docs[0]), asNum);
      }
    } catch (err) {
      console.warn('[dailyActivity] fetchActivityByNumber query failed:', err);
    }
  }

  const all = await dailyActivityService.getAllActivities(true);

  const byTitle = all.find(
    (a) => parseDailyActivityNumberFromTitle(a.title || '') === asNum
  );
  if (byTitle) {
    return applyRequestedActivityNumber(byTitle, asNum);
  }

  const byPage = all.find((a) => parsePageNumberFromImageUrl(a.imageUrl) === asNum);
  if (byPage) {
    return applyRequestedActivityNumber(byPage, asNum);
  }

  if (asNum >= 1 && asNum <= 200) {
    const imageUrl = `${STORAGE_ACTIVITY_PAGE_BASE}/page_${asNum}.png`;
    console.warn('[dailyActivity] Using storage page fallback for activity', asNum);
    return {
      id: asNum,
      number: asNum,
      title: `Daily Activity ${asNum}`,
      description: 'Express your creativity with this engaging art activity',
      instructions: 'Follow the instructions in the activity image to create your artwork',
      duration: '20 minutes',
      materials: 'Paper, pencils, crayons, markers, or any art supplies you prefer',
      difficulty: 'Beginner',
      category: 'Creative Expression',
      tips: 'Let your imagination guide you and enjoy the creative process!',
      imageUrl,
      docId: '',
    };
  }

  console.warn('[dailyActivity] No activity for number', raw);
  return null;
};

export default dailyActivityService;
